import { defineStore } from 'pinia'
import { objectify } from 'radash'

import type { ModuleContext, Module } from '@/types/modules.interfaces'
import { ProductPricePlan } from '@/types/products.interfaces'

interface State {
  installed: Record<string, ModuleContext>
  all: Record<string, Module>
  disableActions: boolean
}

const useModulesStore = defineStore({
  id: 'modules',

  state: (): State => ({
    installed: {},
    all: {},
    disableActions: false,
  }),

  actions: {
    setAllInstalled(installedModules: ModuleContext[]) {
      this.installed = objectify(installedModules, (f) => f.name)
    },

    setModuleLoading(module: Module, loading = true) {
      const m = this.all?.[module.name]
      if (m) m.loading = loading
    },

    setInstalled(module: ModuleContext) {
      this.installed[module.name] = {
        ...this.installed[module.name],
        ...module,
      }

      const modules = useModulesStore()
      modules._linkStates()
    },

    _linkStates() {
      Object.values(this.installed).forEach((moduleContext: ModuleContext) => {
        const module = this.all[moduleContext.name]
        if (module) {
          module.configUrl = moduleContext.config_url
          module.installedVersion = moduleContext.version
          module.status = moduleContext.status || 'uninstalled'
          module.installed =
            moduleContext.status !== undefined &&
            moduleContext.status !== 'uninstalled'
          module.newInstalled = moduleContext.newInstalled
        }
      })
    },

    _cleanDescription() {
      Object.values(this.all).forEach((module: Module) => {
        module.description = module.fullDescription
          .replace(/(\s*)$/, '')
          .replace(/<\/?[^>]+(>|$)/g, '')
        // We removed the trailing space, so check any >= 199 instead of 200
        if (module.description.length >= 199) {
          let truncateAt = module.description.lastIndexOf(' ')
          if (truncateAt === -1) {
            // No space found in the string, let's cut at 195
            truncateAt = 195
          }
          module.description = module.description.substring(0, truncateAt)
          module.description += '&hellip;'
        } else if (/[^.!?]$/.test(module.description)) {
          // Force the desc to be terminated by a . if no punctuation found
          module.description += '.'
        }
      })
    },

    setAll(data: Module[]) {
      this.all = objectify(data, (f) => f.name)

      const modules = useModulesStore()
      modules._linkStates()
      modules._cleanDescription()
    },
  },

  getters: {
    list(state: State): Module[] {
      return Object.values(state.all)
    },

    installedList(): Module[] {
      return this.list.filter((module: Module) => module.installed)
    },

    uninstalledList(): Module[] {
      return this.list.filter(
        (module: Module) => !module.installed || module.newInstalled,
      )
    },
  },
})

export default useModulesStore
